/* eslint-disable */
import { FC, useEffect, useMemo, useState } from 'react'
import { SolveFeedbackModalProps } from './types'
import ModalTemplate from 'uiKit/ModalTemplate'
import { SparklesIcon } from 'uiKit/icons/SparklesIcon'
import * as LocalStyles from './styles'
import styles from './styles.module.scss'
import { useModalToggle } from 'tabs/nlp/components/unansweredQuestions/hooks'
import { QuestionAnswerSection } from './QuestionAnswerSection'
import { useUserFeedback } from '../../context'
import Select from 'react-select'
import SelectorStyles from '../../../../constants/selectorStyles'
import { UnknownQuestionModel } from 'tabs/nlp/components/unansweredQuestions/types'
import * as ModalStyles from '../../../ModalWindow/modal.styles'
import { TrainButtonState } from '../../../unansweredQuestions/util/button'
import { CancelButton, ModalButton } from '../../styles'
import { unknownQuestionService } from 'tabs/nlp/api/unknownQuestions'
import { unansweredQuestionTypeLabels } from 'tabs/nlp/components/unansweredQuestions/configs'

type QuestionTypeOption = {
  value: string,
  label: string
}

export const SolveFeedbackModal: FC<SolveFeedbackModalProps> = ({ feedback }) => {
  const { retrainQuestion, creteNewQuestion } = useUserFeedback()
  const { toggleModal, isOpen } = useModalToggle()

  const [question, setQuestion] = useState(feedback.trainedQuestion)
  const [answer, setAnswer] = useState(feedback.trainedAnswer)
  const [questionTypes, setQuestionTypes] = useState<QuestionTypeOption[]>([])
  const [type, setType] = useState<string>(feedback.trainedType)

  useEffect(() => {
    unknownQuestionService.fetchQuestionTypes().then(questionTypes => {
      const values = questionTypes.datasetTypes
        .filter(value => value !== 'unknown')
        .map(questionType => {
          return { value: questionType, label: unansweredQuestionTypeLabels[questionType] ?? questionType }
        })
      setQuestionTypes(values)
    })
  }, [])

  const isDataValid = useMemo(() => question && answer && type, [question, answer, type])

  const enableCreateButton = useMemo(
    () => isDataValid && question !== feedback.trainedQuestion,
    [isDataValid, question],
  )

  const enableRetrainButton = useMemo(
    () => isDataValid && (answer !== feedback.trainedAnswer || question !== feedback.trainedQuestion),
    [isDataValid, answer, question],
  )

  const onQuestionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setQuestion(e.target.value)
  }

  const onAnswerChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setAnswer(e.target.value)
  }

  const onSelectValueChange = event => {
    setType(event?.value)
  }

  const onRetrainPressed = () => {
    const questionToRetrain: Partial<UnknownQuestionModel> = {
      id: feedback.unknownQuestionId,
      question,
      answer,
      type,
    }
    return retrainQuestion(feedback.id, questionToRetrain).then(toggleModal)
  }

  const onCreatedPressed = () => {
    const newQuestion: Partial<UnknownQuestionModel> = {
      question,
      answer,
      type,
      isTrained: false,
    }
    return creteNewQuestion(feedback.id, newQuestion).then(toggleModal)
  }

  return (
    <>
      <LocalStyles.SolveButton
        onClick={toggleModal}
        state={feedback.solved ? TrainButtonState.TRAINED : TrainButtonState.ACTIVE}>
        {feedback.solved ? 'Solved' : 'Solve'}
      </LocalStyles.SolveButton>
      <ModalTemplate
        containerClasses={styles.modal}
        open={isOpen}
        title="User Feedback Details"
        titleIcon={
          <ModalStyles.TitleIconWrapper>
            <SparklesIcon />
          </ModalStyles.TitleIconWrapper>
        }
        onClose={toggleModal}
        withFooter={false}>
        <ModalStyles.ModalContainer>
          <QuestionAnswerSection
            question={feedback.userQuestion}
            answer={feedback.botAnswer}
            questionTitle="Asked User Question"
            answerTitle="Bot Answer"
          />

          <ModalStyles.Divider />

          <QuestionAnswerSection
            question={feedback.trainedQuestion}
            answer={feedback.trainedAnswer}
            score={feedback.matchScore * 100}
            questionType={feedback.trainedType.toUpperCase()}
            questionTitle="Closest Trained Question"
            answerTitle="Trained Answer"
          />

          {feedback.userFeedback && (
            <>
              <ModalStyles.Divider />

              <ModalStyles.ModalContainerSection>
                <ModalStyles.ModalContainerSectionItem>
                  <ModalStyles.SubHeader>User feedback about bot answer</ModalStyles.SubHeader>
                  <ModalStyles.Text>{feedback.userFeedback}</ModalStyles.Text>
                </ModalStyles.ModalContainerSectionItem>
              </ModalStyles.ModalContainerSection>
            </>
          )}

          <ModalStyles.Divider />

          <ModalStyles.SubHeader>Retrain trained question or create new</ModalStyles.SubHeader>

          <ModalStyles.ModalContainerSection>
            <ModalStyles.ModalContainerSectionItem width="40%">
              <ModalStyles.SubHeader>Question</ModalStyles.SubHeader>
              <LocalStyles.AnswerInput value={question} onChange={onQuestionChange} />
            </ModalStyles.ModalContainerSectionItem>

            <ModalStyles.ModalContainerSectionItem width="58%">
              <ModalStyles.SubHeader>Answer</ModalStyles.SubHeader>
              <LocalStyles.AnswerInput value={answer} onChange={onAnswerChange} />
            </ModalStyles.ModalContainerSectionItem>
            <Select
              styles={SelectorStyles}
              options={questionTypes}
              onChange={onSelectValueChange}
              placeholder="Select question type"
              defaultValue={questionTypes.find(option => option.value === type)}
            />
          </ModalStyles.ModalContainerSection>

          <ModalStyles.ModalButtonContainer>
            <CancelButton onClick={toggleModal}>Cancel</CancelButton>
            <ModalButton
              onClick={onCreatedPressed}
              state={enableCreateButton ? TrainButtonState.ACTIVE : TrainButtonState.INACTIVE}>
              Create new
            </ModalButton>
            <ModalButton
              onClick={onRetrainPressed}
              state={enableRetrainButton ? TrainButtonState.ACTIVE : TrainButtonState.INACTIVE}>
              Retrain
            </ModalButton>
          </ModalStyles.ModalButtonContainer>
        </ModalStyles.ModalContainer>
      </ModalTemplate>
    </>
  )
}
